import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson, ApiError } from '../lib/api'
import { PairTabs, sitesTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { PendingButton, Failed } from '../components/pending'
import { Structure, StructureBoundary, StructureNode, areaText, shownPath } from './sites/structure'
import {
  Edit,
  addPoint,
  coverageLine,
  deletePoint,
  insertPoint,
  isDirty,
  mapTargets,
  movePoint,
  openingView,
  saveBlocked,
  startDraw,
  startReshape,
} from './sites/mapEdit'
import { tileSource } from './sites/mapTiles'
import { MapCanvas, MapShape } from './sites/MapCanvas'
import { GpsYield } from './sites/GpsYield'
import { AddSiteForm, SiteMenu, SiteSettingsDialog, SiteSummary, addedToast } from './sites/SiteMenu'
import { ErrorLine, Foot, Modal } from './SitesPage'
import sm from './sites/SiteMenu.module.css'
import pa from './ProductActivityPage.module.css'
import styles from './SitesPage.module.css'
import mv from './SitesMapPage.module.css'

// Map view (handoff §7g, "Map view"). One Site, one mapping level: the
// location list beside the map holds the nodes at that level, selection is
// synced both ways, and the workspace offers Draw boundary, Reshape, Save,
// Cancel, Remove boundary and Zoom to location with the full path and the
// unsaved state in view. Every save is the server's to judge
// (routes/structure.js: shape, overlap, level, staleness); a refusal is
// shown and the edit stays open. The mapping level itself is changed in Site
// settings, with its impact preview. The GPS yield under the map
// (sites/GpsYield.tsx) reads the picks against the boundaries drawn now.

const err = (e: any, fallback: string) => (e?.message as string) || fallback

const tiles = tileSource()

export const SitesMapPage: React.FC = () => {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const [sites, setSites] = useState<SiteSummary[] | null>(null)
  const [data, setData] = useState<Structure | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [edit, setEdit] = useState<Edit | null>(null)
  const [note, setNote] = useState('')
  const [saveError, setSaveError] = useState('')
  const [busy, setBusy] = useState(false)
  const [dialog, setDialog] = useState<'remove' | 'settings' | null>(null)
  const [zoomTo, setZoomTo] = useState<{ key: number; points: StructureBoundary['points'] } | null>(null)
  const [toast, setToast] = useState('')

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid

  // The list of Sites could not be read: not the same as there being none, so no "create your first site" is offered over it.
  const [sitesFailed, setSitesFailed] = useState(false)
  const loadSites = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    setError(null)
    try {
      setSites(await apiJson<SiteSummary[]>(u, '/api/sites'))
      setSitesFailed(false)
    } catch (e: any) {
      setError(err(e, 'Could not load the sites'))
      setSites((s) => s || [])
      setSitesFailed(true)
    }
  }, [uid])

  useEffect(() => {
    loadSites()
  }, [loadSites])

  const siteId = params.get('site') || sites?.find((s) => !s.archived_at)?.id || sites?.[0]?.id || ''

  // A late answer for the Site left behind must not replace the Site now in view.
  const loadSeq = useRef(0)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u || !siteId) return
    const n = ++loadSeq.current
    try {
      const d = await apiJson<Structure>(u, `/api/sites/${siteId}/structure`)
      if (n !== loadSeq.current) return
      setData(d)
      setError(null)
    } catch (e: any) {
      if (n !== loadSeq.current) return
      setError(err(e, 'Could not load the map'))
    }
  }, [uid, siteId])

  useEffect(() => {
    setData(null)
    setSelected(null)
    setEdit(null)
    setDialog(null)
    load()
  }, [load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 4200)
    return () => clearTimeout(t)
  }, [toast])

  const nodes = data?.nodes || []
  const boundaries = data?.boundaries || []
  const site = data?.site || null
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes])
  const targets = useMemo(() => (site ? mapTargets(nodes, site) : []), [nodes, site])
  const drawn = useMemo(() => new Map(boundaries.map((b) => [b.node_id, b])), [boundaries])
  const hideL2 = site?.show_level2 === false
  const labels = site ? [site.level1_label, site.level2_label, site.level3_label] : ['Level 1', 'Level 2', 'Row']
  const levelLabel = site ? labels[site.map_level - 1] : ''
  const archivedSite = !!site?.archived_at
  const path = (n: StructureNode | undefined | null) => (n ? shownPath(byId, n, hideL2) : '')

  const shapes: MapShape[] = useMemo(
    () => boundaries.map((b) => ({ id: b.id, nodeId: b.node_id, label: byId.get(b.node_id)?.name || '', points: b.points })),
    [boundaries, byId],
  )

  const pick = (id: string) => {
    if (edit) return
    setSelected(id)
    setNote('')
  }

  // Every gesture, and opening an edit, clears the last message.
  const change = (next: Edit) => {
    setEdit(next)
    setNote('')
    setSaveError('')
  }

  const cancel = () => {
    setEdit(null)
    setNote('')
    setSaveError('')
  }

  const save = async () => {
    if (!edit) return
    const node = byId.get(edit.nodeId)
    setBusy(true)
    setSaveError('')
    try {
      const r = await apiJson<{ boundary: StructureBoundary; replaced: string | null }>(user, `/api/structure/${edit.nodeId}/boundary`, {
        method: 'PUT',
        body: { points: edit.points, before: edit.before },
      })
      setEdit(null)
      setToast(`${path(node)}: boundary ${r.replaced ? 'reshaped' : 'drawn'}, ${areaText(r.boundary.area_m2)}.`)
      await load()
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.code : null
      // Someone else's change is kept: this edit cannot be saved over it.
      if (code === 'STALE') {
        setSaveError(`${err(e, 'This boundary changed')}. Cancel to see the boundary as it is now, then draw or reshape again.`)
        await load()
      } else setSaveError(err(e, 'Could not save the boundary'))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = (i: number) => {
    if (!edit) return
    const r = deletePoint(edit, i)
    if (r.refused) setNote(r.refused)
    else change(r.edit)
  }

  if (!sites && !error) {
    return (
      <div className={styles.container}>
        <h1>Sites & jobs</h1>
        <div className={styles.loading}>Loading sites...</div>
      </div>
    )
  }

  const siteAdded = async (s: { id: string; name: string }) => {
    await loadSites()
    setParams({ site: s.id }, { replace: true })
    setToast(addedToast(s.name))
  }

  const sel = selected ? byId.get(selected) || null : null
  const selBoundary = sel ? drawn.get(sel.id) || null : null
  const editNode = edit ? byId.get(edit.nodeId) || null : null
  const unmapped = targets.filter((t) => !drawn.has(t.id))
  const mapped = targets.filter((t) => drawn.has(t.id))
  const blocked = edit ? saveBlocked(edit) : null

  const item = (n: StructureNode) => {
    const b = drawn.get(n.id)
    const on = n.id === (edit?.nodeId || selected)
    return (
      <li key={n.id}>
        <button type="button" className={on ? mv.itemOn : mv.item} aria-pressed={on} disabled={!!edit && !on} onClick={() => pick(n.id)}>
          <span className={mv.itemName}>{path(n)}</span>
          <span className={b ? mv.itemMapped : mv.itemUnmapped}>{b ? areaText(b.area_m2) : 'Unmapped'}</span>
        </button>
      </li>
    )
  }

  return (
    <>
      <PageHeader
        title="Sites & jobs"
        tabs={<PairTabs label="Sites and jobs" tabs={sitesTabs(siteId)} current="Map" />}
      >
        {sites && sites.length > 0 && (
          <SiteMenu
            user={user}
            sites={sites}
            siteId={siteId}
            onPick={(id) => setParams({ site: id }, { replace: true })}
            onAdded={siteAdded}
            onSettings={() => setDialog('settings')}
          />
        )}
      </PageHeader>

      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <button onClick={() => (sitesFailed ? loadSites() : load())}>Retry</button>
        </div>
      )}

      {sitesFailed && !sites?.length ? null : sites && sites.length === 0 ? (
        <PageBody>
          <div className={styles.empty}>
            <p>No sites configured yet</p>
            <p className={styles.emptyHint}>Create your first site to start managing structure and picking jobs</p>
            <AddSiteForm user={user} className={sm.emptyAdd} onAdded={siteAdded} />
          </div>
        </PageBody>
      ) : (
        <PageBody>
          <div className={styles.toolbar}>
            {site && (
              <span className={mv.coverage} data-testid="coverage">
                Mapped by {levelLabel.toLowerCase()} · {coverageLine(targets, boundaries, levelLabel)}
              </span>
            )}
          </div>

          {!data || !site ? (
            !error && <div className={styles.loading}>Loading the map...</div>
          ) : (
            <div className={mv.layout}>
              <aside className={`${pa.card} ${mv.side}`} aria-label="Locations">
                {!targets.length && (
                  <p className={mv.empty}>
                    No active {levelLabel.toLowerCase()} to map yet. Add {labels[2].toLowerCase()}s in Structure.
                  </p>
                )}
                {unmapped.length > 0 && (
                  <>
                    <h2 className={mv.groupHead}>Unmapped ({unmapped.length})</h2>
                    <ul className={mv.list} aria-label="Unmapped">
                      {unmapped.map(item)}
                    </ul>
                  </>
                )}
                {mapped.length > 0 && (
                  <>
                    <h2 className={mv.groupHead}>Mapped ({mapped.length})</h2>
                    <ul className={mv.list} aria-label="Mapped">
                      {mapped.map(item)}
                    </ul>
                  </>
                )}
              </aside>

              <section className={mv.work}>
                <div className={mv.bar} role="toolbar" aria-label="Boundary">
                  {edit && editNode ? (
                    <>
                      <div className={mv.barText}>
                        <div className={mv.barPath}>{path(editNode)}</div>
                        <div className={mv.barMeta}>
                          {edit.mode === 'draw' ? 'Drawing a boundary' : 'Reshaping the boundary'} · {edit.points.length} point{edit.points.length === 1 ? '' : 's'}
                          {isDirty(edit) && <strong className={mv.unsaved}> · Unsaved changes</strong>}
                        </div>
                      </div>
                      <button type="button" className={styles.ghostBtn} onClick={cancel} disabled={busy}>
                        Cancel
                      </button>
                      <PendingButton type="button" className={pa.primaryBtn} busy={busy} pending="Saving…" onClick={save} disabled={busy || !!blocked} title={blocked || undefined}>
                        Save
                      </PendingButton>
                      <Failed text={saveError} />
                    </>
                  ) : sel ? (
                    <>
                      <div className={mv.barText}>
                        <div className={mv.barPath}>{path(sel)}</div>
                        <div className={mv.barMeta}>
                          {levelLabel} · {selBoundary ? `Mapped, ${areaText(selBoundary.area_m2)}, ${selBoundary.points.length} points` : 'Unmapped'}
                        </div>
                      </div>
                      {selBoundary ? (
                        <>
                          <button type="button" className={styles.ghostBtn} onClick={() => setZoomTo({ key: Date.now(), points: selBoundary.points })}>
                            Zoom to location
                          </button>
                          <button type="button" className={styles.ghostBtn} disabled={archivedSite} onClick={() => setDialog('remove')}>
                            Remove boundary
                          </button>
                          <button type="button" className={pa.primaryBtn} disabled={archivedSite} onClick={() => change(startReshape(selBoundary))}>
                            Reshape
                          </button>
                        </>
                      ) : (
                        <button type="button" className={pa.primaryBtn} disabled={archivedSite} onClick={() => change(startDraw(sel.id))}>
                          Draw boundary
                        </button>
                      )}
                    </>
                  ) : (
                    <div className={mv.barMeta}>Choose a {levelLabel.toLowerCase()} in the list or on the map.</div>
                  )}
                </div>

                {edit && (
                  <p className={mv.hint}>
                    {edit.mode === 'draw' ? 'Left-click to place each point. ' : ''}Drag a point to move it, drag a green midpoint to add one, double-click a point to delete it.
                    Right-click and drag to pan; the wheel zooms. Cancel discards the whole edit.
                  </p>
                )}
                {note && (
                  <p className={mv.note} role="status">
                    {note}
                  </p>
                )}
                {edit && blocked && edit.points.length > 0 && blocked !== 'Nothing has changed' && <p className={mv.hint}>{blocked}</p>}
                {archivedSite && <p className={mv.hint}>This site is archived, so its map is read-only.</p>}
                {!site.map_centre && !boundaries.length && (
                  <p className={mv.hint}>
                    This site has no map centre yet.{' '}
                    <button type="button" className={mv.link} onClick={() => setDialog('settings')}>
                      Set one in Site settings
                    </button>{' '}
                    so the map opens on it.
                  </p>
                )}

                <MapCanvas
                  key={site.id}
                  tiles={tiles}
                  opening={openingView(site, boundaries)}
                  shapes={shapes}
                  selected={selected}
                  edit={edit}
                  zoomTo={zoomTo}
                  onSelect={pick}
                  onAdd={(p) => edit && change(addPoint(edit, p))}
                  onMove={(i, p) => edit && change(movePoint(edit, i, p))}
                  onInsert={(i, p) => edit && change(insertPoint(edit, i, p))}
                  onDelete={onDelete}
                />
                {!tiles.configured && <p className={mv.hint}>Street map: no satellite imagery provider is configured yet.</p>}
              </section>
            </div>
          )}
          {data && site && (
            <GpsYield user={user} siteId={site.id} levelLabel={levelLabel} version={boundaries.map((b) => b.id).join()} onSelect={(id) => {
              pick(id)
              const b = drawn.get(id)
              if (b && !edit) setZoomTo({ key: Date.now(), points: b.points })
            }} />
          )}
        </PageBody>
      )}

      {dialog === 'remove' && sel && selBoundary && (
        <RemoveDialog
          user={user}
          node={sel}
          path={path(sel)}
          boundary={selBoundary}
          onClose={() => setDialog(null)}
          onDone={async (m) => {
            setDialog(null)
            setToast(m)
            await load()
          }}
          onStale={async (m) => {
            setDialog(null)
            setNote(m)
            await load()
          }}
        />
      )}

      {dialog === 'settings' && (
        <SiteSettingsDialog
          user={user}
          siteId={siteId}
          onClose={() => setDialog(null)}
          onSaved={async (_s, m) => {
            setDialog(null)
            setToast(m)
            await loadSites()
            await load()
          }}
        />
      )}

      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

// "Archiving or clearing removes a polygon from active attribution; retain
// an audit record or recoverable prior geometry": the shape stays on record.
function RemoveDialog({
  user,
  node,
  path,
  boundary,
  onClose,
  onDone,
  onStale,
}: {
  user: any
  node: StructureNode
  path: string
  boundary: StructureBoundary
  onClose: () => void
  onDone: (message: string) => void
  // Someone else changed it: nothing was removed, and the map is read again.
  onStale: (message: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const remove = async () => {
    setBusy(true)
    setError('')
    try {
      await apiJson(user, `/api/structure/${node.id}/boundary/${boundary.id}`, { method: 'DELETE' })
      onDone(`${path}: boundary removed. The shape stays on record.`)
    } catch (e: any) {
      setBusy(false)
      if (e instanceof ApiError && e.body?.code === 'STALE') onStale(`${err(e, 'This boundary changed')}. Nothing was removed; the map shows it as it is now.`)
      else setError(err(e, 'Could not remove the boundary'))
      return
    }
    setBusy(false)
  }
  return (
    <Modal title="Remove boundary" busy={busy} onClose={onClose}>
      <p>
        Remove the boundary of <strong>{path}</strong> ({areaText(boundary.area_m2)})? It stops counting for GPS attribution and the location reads Unmapped. The
        shape stays on record in History.
      </p>
      <Foot busy={busy} label="Remove boundary" pending="Removing…" danger error={error} onClose={onClose} onSubmit={remove} />
    </Modal>
  )
}
